'use client';

// Standalone Jigyasa SaaS shell: session check → auth screens or workspace.
// The workspace mounts the same engine the demo uses, but with the
// API-backed storage adapter and the org's own costume.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  LogOut,
  Settings2,
  LayoutDashboard,
  TrendingUp,
  Users,
  Briefcase,
  Footprints,
  Settings,
  Upload,
  Menu,
  X,
  ChevronDown,
  Building2,
  Tag,
  ListOrdered,
  XCircle,
  Layers,
  CreditCard,
  Activity,
} from "lucide-react";
import { api } from "./api";
import { AuthScreen } from "./AuthScreen";
import { Homepage } from "./Homepage";
import { StateProvider } from "../jigyasa/state";
import { OperatorDesktop } from "../jigyasa/OperatorDesktop";
import { OwnerPhone } from "../jigyasa/OwnerPhone";
import { ManageSourcesSheet, ManageTypesSheet, ManageLostReasonsSheet, ManageStatusesSheet, ManageLinesSheet } from "../jigyasa/settings";
import { EnquiryFlowSheet } from "./EnquiryFlowSheet";
import { createApiStorage } from "../jigyasa/apiStorage";
import TeamPulse from "./TeamPulse";
import { standaloneCostume } from "../jigyasa/costumes/registry";
import { TeamModule } from "../packages/team";
import { createTeamStorage } from "./teamStorage";
import { OrgSettingsModule } from "../packages/org-settings";
import { createOrgSettingsStorage } from "./orgSettingsStorage";
import { LightAccountsModule } from "../packages/light-accounts";
import { createAccountsStorage } from "./accountsStorage";
import { IndividualsModule } from "../packages/individuals";
import { createIndividualsStorage } from "./individualsStorage";
import { TrialBanner, UpgradeModal, TrialExpired, MyPlanSheet, RenewalBanner } from "./Billing";
import { OnboardingChecklist } from "./OnboardingChecklist";
import { AshraryaProvider, AshraryaWidget, ResumeBanner } from "../packages/aumonline-ashraya/src";
import { AshrayaIntroLauncher } from "./AshrayaIntroLauncher";
import { AshrayaGuideLauncher } from "./AshrayaGuideLauncher";
import { InstallAppButton } from "./InstallApp";

const SETTINGS_TOUR_TARGETS = ["nav-settings", "nav-flow", "nav-statuses", "nav-types",
  "nav-sources", "nav-lost_reasons", "nav-lines", "nav-import"];
const FLOW_SHEET_TOUR_TARGETS = ["flow-steps", "flow-types", "flow-sources", "flow-lost"];
// Targets that only exist on the Work desk (operator view). If a tour step
// points at one while the user is on another view (e.g. Settings, where the
// intro tours walk them first), we must bring them back or the tour waits
// forever on an element that can never appear.
const WORK_DESK_TOUR_TARGETS = ["jigyasa-new-enquiry-btn", "jigyasa-due-today",
  "jigyasa-lead-card", "line-switcher"];
// Intro tours only: the right panel must FOLLOW whatever screen the guide is
// describing — never talk about a room the person can't see (owner-reported,
// solo tour steps 6-7 showed the Work desk while describing Business view).
const INTRO_FOLLOW_TOURS = ["jigyasa-tour", "jigyasa-tour-solo", "jigyasa-tour-staff"];
const TOUR_VIEW_TARGETS = {
  "nav-operator": "operator", "nav-owner": "owner", "nav-accounts": "accounts",
  "nav-walkins": "walkins", "nav-settings": "settings",
};
const TOUR_EDITOR_TARGETS = {
  "nav-statuses": "statuses", "nav-types": "types", "nav-sources": "sources",
  "nav-lost_reasons": "lost_reasons", "nav-lines": "lines",
};
import { ImportData } from "./ImportData";

export default function StandaloneApp() {
  const [session, setSession] = useState(undefined); // undefined = checking
  // Deep link: /?plan=team-yearly opens signup with that plan pre-selected.
  const [authMode, setAuthMode] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("plan") ? "signup" : null;
    } catch { return null; }
  });
  const [signupPlan, setSignupPlan] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("plan") || null; } catch { return null; }
  });

  const startSignup = (plan) => {
    const p = typeof plan === "string" ? plan : null;
    setSignupPlan(p);
    try {
      window.history.replaceState(null, "", p ? `?plan=${encodeURIComponent(p)}` : window.location.pathname);
    } catch { /* older browsers: state alone still works */ }
    setAuthMode("signup");
  };

  // Leaving the signup flow (logged in, logged out, or back to homepage) must
  // also drop the ?plan deep-link — otherwise a later logout/refresh re-opens
  // the signup screen instead of the landing page.
  const clearAuthEntry = () => {
    setAuthMode(null);
    setSignupPlan(null);
    try { window.history.replaceState(null, "", window.location.pathname); } catch {}
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setSession(r.data))
      .catch(() => setSession(null));
  }, []);

  if (session === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <div className="text-sm tracking-widest text-zinc-500">JIGYASA</div>
      </div>
    );
  }

  if (!session) {
    if (authMode) {
      return <AuthScreen initialMode={authMode} plan={signupPlan} onAuthed={(s) => { clearAuthEntry(); setSession(s); }} onHome={clearAuthEntry} />;
    }
    return <Homepage onLogin={() => setAuthMode("login")} onStart={startSignup} />;
  }

  return (
    <Workspace
      session={session}
      onSessionRefresh={() =>
        api.get("/auth/me").then((r) => setSession(r.data)).catch(() => {})
      }
      onLogout={async () => {
        try {
          await api.post("/auth/logout");
        } catch {
          // even if the call fails, drop the local session
        }
        clearAuthEntry();
        setSession(null);
      }}
    />
  );
}

// Day-to-day items live at the top level; everything you set up once lives
// under the "Settings" accordion (owner decision, 16 Jun 2026).
const TOP_NAV = [
  { key: "operator", label: "Work desk", icon: LayoutDashboard },
  { key: "owner", label: "Business view", icon: TrendingUp },
  { key: "team", label: "Team", icon: Users },
  { key: "pulse", label: "Team pulse", icon: Activity },
  { key: "accounts", label: "Customers", icon: Briefcase },
  { key: "walkins", label: "Individual", icon: Footprints },
];

// Children of the Settings accordion. kind "view" opens a page, "sheet" opens
// an editor drawer, "soon" is a disabled placeholder (Statuses editor — later).
const SETTINGS_CHILDREN = [
  { key: "settings", label: "Organisation settings", icon: Building2, kind: "view" },
  { key: "lines", label: "Lines of business", icon: Layers, kind: "sheet" },
  { key: "sources", label: "Where people find you", icon: Settings2, kind: "sheet" },
  { key: "types", label: "What people ask for", icon: Tag, kind: "sheet" },
  { key: "statuses", label: "Follow-up steps", icon: ListOrdered, kind: "sheet" },
  { key: "lost_reasons", label: "Lost reasons", icon: XCircle, kind: "sheet" },
  { key: "import", label: "Import data", icon: Upload, kind: "view" },
];

const initials = (name) =>
  (name || "?").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();

const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const Workspace = ({ session, onLogout, onSessionRefresh }) => {
  const { user, org } = session;
  const billing = session.billing || { status: "active", locked: false };
  const isAdmin = ["owner", "admin"].includes(user.role);
  // The simplified, one-person experience applies to the Solo plan AND to any
  // fresh trial (plan not yet chosen). Team and grandfathered orgs (plan
  // "team") keep the full interface. This is what a new Wekreate Properties
  // pilot user sees from their very first login.
  const isSolo = !String(billing.plan || "").startsWith("team");
  const [view, setView] = useState("operator");
  const [flowOpen, setFlowOpen] = useState(false);
  // Bumped to ask the work desk to pop open the "New enquiry" form (used by the
  // onboarding checklist's "Add one now" CTA).
  const [addEnquiryToken, setAddEnquiryToken] = useState(0);

  // The checklist navigates to a view; for "Add your first enquiry" we also want
  // the New-enquiry form to actually open, not just land on the (already-open) desk.
  // "flow-guide" fires the Ashraya guided walkthrough of the enquiry-flow setup.
  const handleChecklistNav = (go) => {
    if (go === "flow-guide") {
      if (ashrayaEnabled) {
        window.dispatchEvent(new CustomEvent("jigyasa:launch-flow-guide"));
      } else if (isSolo) {
        setFlowOpen(true);
      } else {
        setSettingsExpanded(true);
        setView("settings");
      }
      return;
    }
    setView(go);
    if (go === "operator") setAddEnquiryToken((t) => t + 1);
  };
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [typesOpen, setTypesOpen] = useState(false);
  const [lostReasonsOpen, setLostReasonsOpen] = useState(false);
  const [statusesOpen, setStatusesOpen] = useState(false);
  const [linesOpen, setLinesOpen] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [myPlanOpen, setMyPlanOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Phones: the Ashraya bubble steps aside while the menu drawer is open —
  // it was sitting on top of menu rows (owner-reported).
  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth >= 640) return undefined;
    if (!mobileNavOpen) return undefined;
    document.body.dataset.ashrayaShyNav = "true";
    return () => { delete document.body.dataset.ashrayaShyNav; };
  }, [mobileNavOpen]);
  const [logoBroken, setLogoBroken] = useState(false);
  const costume = useMemo(() => standaloneCostume(org.costume_id), [org.costume_id]);
  const storage = useMemo(() => createApiStorage(), []);

  // ── Team operating core (senior + staff) ─────────────────────────────────
  // The senior can look at anyone's work; staff arrive already scoped by the
  // server. viewingId: "all" | <user_id> | "none" (unassigned).
  const [teamMembers, setTeamMembers] = useState([]);
  const [viewingId, setViewingId] = useState("all");
  useEffect(() => {
    if (isSolo || !isAdmin) return;
    api.get("/team/members")
      .then((r) => {
        const rows = (Array.isArray(r.data) ? r.data : []).filter((m) => m.status === "active" && m.user_id);
        setTeamMembers(rows.map((m) => ({ user_id: m.user_id, name: m.name, role: m.role })));
      })
      .catch(() => {});
  }, [isSolo, isAdmin]);
  const hasStaff = teamMembers.some((m) => m.role === "staff");
  const scopedStorage = useMemo(() => {
    if (viewingId === "all") return storage;
    return {
      ...storage,
      loadEnquiries: async () => {
        const all = await storage.loadEnquiries();
        if (viewingId === "none") return all.filter((e) => !e.assigned_to);
        return all.filter((e) => e.assigned_to === viewingId);
      },
    };
  }, [storage, viewingId]);
  const moveAllTo = async (toId) => {
    if (!toId) return;
    const from = teamMembers.find((m) => m.user_id === viewingId);
    const to = teamMembers.find((m) => m.user_id === toId);
    const fromName = viewingId === "none" ? "unassigned enquiries" : `all of ${from?.name || "this person"}'s enquiries`;
    if (!window.confirm(`Move ${fromName} to ${to?.name}?`)) return;
    try {
      await api.post("/enquiries/reassign-bulk", {
        from_user_id: viewingId === "none" ? "" : viewingId,
        to_user_id: toId,
      });
      setViewingId(toId);
    } catch { /* the viewing bar simply stays put */ }
  };
  const teamStorage = useMemo(() => createTeamStorage(), []);
  const orgSettingsStorage = useMemo(() => createOrgSettingsStorage(), []);
  const accountsStorage = useMemo(() => createAccountsStorage(), []);
  const individualsStorage = useMemo(() => createIndividualsStorage(), []);

  const logoVersion = org.profile?.logo?.updated_at;
  const logoUrl = useMemo(
    () => (logoVersion ? orgSettingsStorage.logoUrl(logoVersion) : null),
    [logoVersion, orgSettingsStorage]
  );
  useEffect(() => setLogoBroken(false), [logoVersion]);

  // Tour self-heal: if an Ashraya step targets anything inside the Settings
  // accordion or the enquiry-flow sheet, force that container open — a stray
  // toggle/close would otherwise leave the tour waiting on a hidden element.
  // Exception: while the user is inside one of the four editors the guide
  // points at (stages/types/sources/lost), the tour waits quietly instead of
  // fighting them; the flow sheet reopens the moment they close the editor.
  // One voice at a time: while any Ashraya guide is running, the onboarding
  // checklist card steps aside so two helpers never talk over each other.
  const [tourRunning, setTourRunning] = useState(false);
  const editorsOpen = statusesOpen || typesOpen || sourcesOpen || lostReasonsOpen || linesOpen;
  const editorsOpenRef = useRef(editorsOpen);
  const tourTargetRef = useRef(null);
  // True while an intro tour is driving the screens itself. The editor sheets
  // it opens are look-only (fence blocks them), so the usual "editor open →
  // pause the guide" rule must not fire.
  const introFollowRef = useRef(false);
  useEffect(() => {
    const onTourEvent = (e) => {
      const d = e.detail || {};
      if (d.type === "step_started") {
        tourTargetRef.current = d.target_id || null;
        setTourRunning(true);
        introFollowRef.current = INTRO_FOLLOW_TOURS.includes(d.tour_id);
      }
      if (d.type === "tour_completed" || d.type === "tour_abandoned") {
        tourTargetRef.current = null;
        setTourRunning(false);
        introFollowRef.current = false;
        // The team setup guide navigates into Settings views; never leave the
        // user parked there when the guide ends or is skipped.
        if (d.tour_id === "jigyasa-flow-setup-team") setView("operator");
        // Intro tours walk through several screens; always hand the person
        // back to the Work desk with every panel closed.
        if (INTRO_FOLLOW_TOURS.includes(d.tour_id)) {
          setView("operator");
          setFlowOpen(false);
          setStatusesOpen(false); setTypesOpen(false); setSourcesOpen(false);
          setLostReasonsOpen(false); setLinesOpen(false);
        }
        // Phones: never leave the menu drawer hanging open after any guide.
        setMobileNavOpen(false);
      }
      if (d.type !== "step_started" && d.type !== "target_missing") return;
      if (SETTINGS_TOUR_TARGETS.includes(d.target_id)) setSettingsExpanded(true);
      if (WORK_DESK_TOUR_TARGETS.includes(d.target_id)) setView("operator");
      // Phones: the sidebar lives in a slide-out drawer. Any guide step that
      // points at a menu item must open the drawer first (or it spotlights a
      // closed door); steps pointing at the main screen close it again.
      if (typeof d.target_id === "string" && typeof window !== "undefined" && window.innerWidth < 640) {
        const inSidebar = d.target_id === "workspace-sidebar" || d.target_id.startsWith("nav-");
        setMobileNavOpen(inSidebar);
      }
      // Intro tours: show the screen being described. Flow-setup and other
      // guides are untouched — there the user opens things by tapping.
      if (INTRO_FOLLOW_TOURS.includes(d.tour_id)) {
        const t = d.target_id;
        // On phones the editor/flow sheets would bury the drawer that holds
        // the spotlighted menu item — the highlighted row itself is the show.
        const isPhone = typeof window !== "undefined" && window.innerWidth < 640;
        const editorKey = isPhone ? null : TOUR_EDITOR_TARGETS[t] || null;
        const managed = TOUR_VIEW_TARGETS[t] || TOUR_EDITOR_TARGETS[t] || t === "nav-flow" ||
          WORK_DESK_TOUR_TARGETS.includes(t) || t === "ashraya-bubble";
        if (managed) {
          setStatusesOpen(editorKey === "statuses");
          setTypesOpen(editorKey === "types");
          setSourcesOpen(editorKey === "sources");
          setLostReasonsOpen(editorKey === "lost_reasons");
          setLinesOpen(editorKey === "lines");
          setFlowOpen(t === "nav-flow" && !isPhone);
          if (TOUR_VIEW_TARGETS[t]) setView(TOUR_VIEW_TARGETS[t]);
        }
      }
      // Relay finale: when a step spotlights a card inside the Ashraya panel,
      // close the flow sheet so it doesn't hang around behind the handoff.
      if (typeof d.target_id === "string" && d.target_id.startsWith("ashraya-card-")) setFlowOpen(false);
      if (FLOW_SHEET_TOUR_TARGETS.includes(d.target_id)) {
        // Deferred: when a row-click both opens an editor AND advances the
        // tour, React hasn't committed yet — an immediate ref check reads a
        // stale "no editor open" and wrongly resurrects the flow sheet
        // behind the editor. Re-check after commit.
        setTimeout(() => { if (!editorsOpenRef.current) setFlowOpen(true); }, 80);
      }
    };
    window.addEventListener("ashraya:tour", onTourEvent);
    return () => window.removeEventListener("ashraya:tour", onTourEvent);
  }, []);
  useEffect(() => {
    editorsOpenRef.current = editorsOpen;
    if (editorsOpen && !introFollowRef.current) document.body.dataset.ashrayaPaused = "true";
    else delete document.body.dataset.ashrayaPaused;
    if (!editorsOpen && FLOW_SHEET_TOUR_TARGETS.includes(tourTargetRef.current)) setFlowOpen(true);
    return () => { delete document.body.dataset.ashrayaPaused; };
  }, [editorsOpen]);

  const orgInitials = initials(org.name);
  const roleLabel = titleCase(user.role || "owner");
  const orgAddress = useMemo(() => {
    const p = org.profile || {};
    return [p.address, p.city, p.state, p.pincode].filter(Boolean).join(", ");
  }, [org.profile]);

  // Solo sees only the few things a one-person business needs; everything
  // team/multi-board related is hidden. Team orgs keep the full menu.
  const navItems = isSolo
    ? [
        { key: "operator", label: "Work desk", icon: LayoutDashboard },
        { key: "owner", label: "Business view", icon: TrendingUp },
        { key: "walkins", label: "My customers", icon: Users },
      ]
    : TOP_NAV.filter((i) =>
        user.role === "staff" ? i.key !== "team" && i.key !== "pulse" : true
      );
  const settingsChildren = [
    ...(isSolo
      ? [
          { key: "settings", label: "My business details", icon: Building2, kind: "view" },
          { key: "flow", label: "How my enquiries flow", icon: ListOrdered, kind: "flow" },
        ]
      : SETTINGS_CHILDREN),
    // Real paying customers (active + paid_until) get a "My plan" entry —
    // what they're on, when it ends, renew, and upgrade-only options.
    ...(isAdmin && billing.status === "active" && billing.paid_until
      ? [{ key: "myplan", label: "My plan", icon: CreditCard, kind: "myplan" }]
      : []),
  ];

  const openTop = (item) => {
    setView(item.key);
    setMobileNavOpen(false);
  };

  const openChild = (item) => {
    if (item.kind === "soon") return;
    if (item.kind === "myplan") {
      setMyPlanOpen(true);
      setMobileNavOpen(false);
      return;
    }
    if (item.kind === "flow") {
      setFlowOpen(true);
      setMobileNavOpen(false);
      return;
    }
    if (item.kind === "sheet") {
      if (item.key === "sources") setSourcesOpen(true);
      else if (item.key === "types") setTypesOpen(true);
      else if (item.key === "lost_reasons") setLostReasonsOpen(true);
      else if (item.key === "statuses") setStatusesOpen(true);
      else if (item.key === "lines") setLinesOpen(true);
    } else {
      setView(item.key);
    }
    setMobileNavOpen(false);
  };

  if (billing.locked) {
    return <TrialExpired onLogout={onLogout} billing={billing} />;
  }

  const readOnly = !!session.user?.read_only;

  // Ashraya in-app guide — mounted only when explicitly enabled and a webhook
  // (n8n entry point) is configured. Off by default so production is untouched
  // until the help content + n8n workflow are ready.
  const ashrayaEnabled =
    process.env.REACT_APP_ASHRAYA_ENABLED === "true" &&
    !!process.env.REACT_APP_ASHRAYA_WEBHOOK_URL;
  const ashrayaConfig = {
    appNamespace: "jigyasa",
    appName: "Jigyasa",
    webhookUrl: process.env.REACT_APP_ASHRAYA_WEBHOOK_URL,
    user: { id: user.id || user.phone || "anon", name: user.name, role: user.role || "owner", org_id: user.org_id },
    variant: user.role === "staff" ? "staff" : isSolo ? "solo" : "full",
    language: "en",
    theme: { primary: "#e11d48" },
    hideOnPaths: [],
    debug: process.env.REACT_APP_ASHRAYA_DEBUG === "true",
    // Direct-to-backend testing only: lets the widget send the shared secret.
    // In the n8n-conductor setup this is unset and n8n holds the secret.
    requestHeaders: process.env.REACT_APP_ASHRAYA_SECRET
      ? { "X-Ashraya-Secret": process.env.REACT_APP_ASHRAYA_SECRET }
      : undefined,
    // Staff never see the owner's setup chip — their chips are about THEIR
    // work: tour, add an enquiry, follow up.
    suggestions: user.role === "staff"
      ? [
          { label: "\uD83D\uDE80 Tour of Jigyasa", text: "give me a tour", tourId: "ashraya-card-tour" },
          { label: "\u2795 Add an enquiry", text: "How do I add a lead?", tourId: "ashraya-card-add" },
          { label: "\uD83D\uDCDE How do I follow up?", text: "How do I follow up?", tourId: "ashraya-card-followup" },
        ]
      : [
          { label: "\uD83D\uDE80 Tour of Jigyasa", text: "give me a tour", tourId: "ashraya-card-tour" },
          { label: "\uD83D\uDEE0\uFE0F Set up my enquiries flow", text: "set up my enquiry flow", tourId: "ashraya-card-flow" },
          { label: "\u2795 Add my first enquiry", text: "How do I add a lead?", tourId: "ashraya-card-add" },
        ],
  };

  const workspace = (
    <StateProvider
      key={`ws-${viewingId}`}
      storage={scopedStorage}
      costume={costume}
      currentUser={{ id: user.id, name: user.name, role: user.role }}
      teamMembers={!isSolo && isAdmin && teamMembers.length > 0 ? teamMembers : null}
      companyInfo={{ name: org.name, me: user.name, address: orgAddress }}
      lookupTrigger="auto"
      enableLines={!isSolo}
    >
      <div
        className="flex min-h-screen bg-zinc-50 text-zinc-900"
        style={{ fontFamily: "'Hind', 'IBM Plex Sans', system-ui, sans-serif" }}
      >
        {/* Sidebar (desktop: always visible; mobile: drawer) */}
        <aside
          data-testid="workspace-sidebar"
          data-tour-id="workspace-sidebar"
          style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
          className={`fixed inset-y-0 left-0 z-30 flex w-64 shrink-0 flex-col border-r border-zinc-200 bg-white transition-transform sm:sticky sm:top-0 sm:h-screen sm:translate-x-0 ${
            mobileNavOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Brand block — matches the Kreta layout:
              org logo → org name → Powered by JIGYASA → Aumonline → user */}
          <div className="relative flex flex-col items-center gap-2 border-b border-zinc-200 px-4 py-5 text-center">
            <button
              type="button"
              data-testid="mobile-nav-close"
              onClick={() => setMobileNavOpen(false)}
              className="absolute right-2 top-2 rounded-md p-1 text-zinc-500 hover:bg-zinc-100 sm:hidden"
            >
              <X size={18} />
            </button>

            {/* 1 + 2: subscribing organisation's logo and name */}
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
              {logoUrl && !logoBroken ? (
                <img
                  data-testid="sidebar-org-logo"
                  src={logoUrl}
                  alt={org.name}
                  onError={() => setLogoBroken(true)}
                  className="h-full w-full object-contain p-2"
                />
              ) : (
                <span className="text-2xl font-extrabold text-zinc-300">{orgInitials}</span>
              )}
            </div>
            <div data-testid="workspace-org-name" className="px-1 text-xl font-bold leading-tight tracking-tight text-zinc-900">
              {org.name}
            </div>

            {/* 3: powered by Jigyasa (our engine) */}
            <div className="flex flex-col items-center pt-1">
              <div className="mb-2 h-px w-14 bg-zinc-200" />
              <span className="text-[11px] text-zinc-400">Powered by</span>
              <span className="text-sm font-bold tracking-[0.12em] text-zinc-700">JIGYASA</span>
            </div>

            {/* 4: Aumonline — the maker of the app */}
            <img
              src="/aumonline-logo.png"
              alt="Aumonline"
              className="mt-0.5 h-6 w-auto opacity-90"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />

            {/* 5: the logged-in user + their role */}
            <div className="pt-1">
              <div data-testid="sidebar-user-name" className="text-sm font-semibold text-zinc-900">{user.name}</div>
              <div data-testid="sidebar-user-role" className="text-xs text-zinc-500">{roleLabel}</div>
            </div>
          </div>

          {/* Nav items */}
          <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = view === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  data-testid={`nav-${item.key}`}
                  data-tour-id={`nav-${item.key}`}
                  onClick={() => openTop(item)}
                  className={`group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-rose-50 text-rose-700 font-semibold" : "text-zinc-700 hover:bg-zinc-100"
                  }`}
                  title={item.label}
                >
                  <Icon size={16} />
                  <span className="flex-1 text-left">{item.label}</span>
                </button>
              );
            })}

            {/* Settings accordion: everything you set up once lives in here.
                Staff don't get it at all — settings define the business,
                which is the senior's domain (owner decision, 8 Aug 2026). */}
            {user.role !== "staff" && (
              <>
            <button
              type="button"
              data-testid="nav-settings-parent"
              data-tour-id="nav-settings-parent"
              onClick={() => setSettingsExpanded((s) => !s)}
              className="group mt-1 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100"
            >
              <Settings size={16} />
              <span className="flex-1 text-left">Settings</span>
              <ChevronDown
                size={15}
                className={`text-zinc-400 transition-transform ${settingsExpanded ? "rotate-180" : ""}`}
              />
            </button>

            {settingsExpanded && (
              <div className="space-y-0.5 border-l border-zinc-100 pl-3">
                {settingsChildren.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.kind === "view" && view === item.key;
                  const disabled = item.kind === "soon";
                  return (
                    <button
                      key={item.key}
                      type="button"
                      data-testid={`nav-${item.key}`}
                      data-tour-id={`nav-${item.key}`}
                      disabled={disabled}
                      onClick={() => openChild(item)}
                      className={`group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-rose-50 text-rose-700 font-semibold"
                          : disabled
                          ? "cursor-not-allowed text-zinc-400"
                          : "text-zinc-700 hover:bg-zinc-100"
                      }`}
                      title={disabled ? "Coming soon" : item.label}
                    >
                      <Icon size={15} />
                      <span className="flex-1 text-left">{item.label}</span>
                      {disabled && (
                        <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 group-hover:bg-zinc-200">
                          Soon
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
              </>
            )}
          </nav>

          {/* Install on home screen — owner/admin only, and never while a
              guide is running (a misplaced ring must not arm it). */}
          {!tourRunning && <InstallAppButton isAdmin={isAdmin} />}

          {/* Logout */}
          <div className="border-t border-zinc-200 p-3">
            <button
              type="button"
              data-testid="logout-button"
              onClick={onLogout}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100"
            >
              <LogOut size={15} />
              Log out
            </button>
          </div>
        </aside>

        {/* Backdrop for mobile drawer */}
        {mobileNavOpen && (
          <div
            className="fixed inset-0 z-20 bg-black/40 sm:hidden"
            onClick={() => setMobileNavOpen(false)}
          />
        )}

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Free-trial banner (only while on trial) */}
          <TrialBanner billing={billing} onUpgrade={() => setUpgradeOpen(true)} />

          {/* Renewal reminder for paying customers in their last 7 days */}
          <RenewalBanner billing={billing} onRenew={() => setMyPlanOpen(true)} />

          {/* Senior's viewing bar — look at anyone's work, move it in bulk */}
          {!isSolo && isAdmin && hasStaff && (
            <div
              data-testid="viewing-bar"
              className={`flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2 text-sm ${viewingId === "all" ? "border-zinc-200 bg-white" : "border-sky-200 bg-sky-50"}`}
            >
              <span className="font-medium text-zinc-600">Viewing:</span>
              <select
                data-testid="viewing-select"
                value={viewingId}
                onChange={(e) => setViewingId(e.target.value)}
                className="rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm"
              >
                <option value="all">Everyone's work</option>
                {teamMembers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.user_id === user.id ? `${m.name} (you)` : m.name}
                  </option>
                ))}
                <option value="none">Not assigned to anyone</option>
              </select>
              {viewingId !== "all" && (
                <>
                  <span data-testid="viewing-banner-note" className="text-sky-800">
                    You're looking at {viewingId === "none" ? "unassigned enquiries" : `${teamMembers.find((m) => m.user_id === viewingId)?.name}'s desk`} — board, calls and numbers are theirs.
                  </span>
                  <select
                    data-testid="bulk-move-select"
                    value=""
                    onChange={(e) => moveAllTo(e.target.value)}
                    className="ml-auto rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm"
                  >
                    <option value="">Move all this work to…</option>
                    {teamMembers.filter((m) => m.user_id !== viewingId).map((m) => (
                      <option key={m.user_id} value={m.user_id}>{m.name}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
          )}

          {/* Look-only support session (God Panel impersonation) */}
          {readOnly && (
            <div
              data-testid="readonly-banner"
              className="border-b border-amber-300 bg-amber-100 px-4 py-2 text-center text-sm font-medium text-amber-900"
            >
              Support view — read only. You're seeing this account as the customer sees it; changes are disabled.
            </div>
          )}

          {/* Mobile-only top bar with hamburger */}
          <header
            data-testid="workspace-mobile-header"
            className="sticky top-0 z-10 flex items-center gap-3 border-b border-zinc-200 bg-white px-3 py-2 sm:hidden"
          >
            <button
              type="button"
              data-testid="mobile-nav-open"
              onClick={() => setMobileNavOpen(true)}
              className="rounded-md p-1 text-zinc-700 hover:bg-zinc-100"
            >
              <Menu size={18} />
            </button>
            <span className="font-semibold tracking-tight">{org.name}</span>
            <span className="truncate text-xs text-zinc-500">· Jigyasa</span>
          </header>

          {/* The actual page body */}
          <div className="flex-1">
            {view === "operator" ? (
              <div>
                {billing.status === "trial" && isAdmin && (
                  <div className="px-2 pt-4 sm:px-6">
                    {!tourRunning && (
                      <OnboardingChecklist org={org} onNavigate={handleChecklistNav} isSolo={isSolo} />
                    )}
                  </div>
                )}
                <OperatorDesktop autoOpenAdd={addEnquiryToken} />
              </div>
            ) : view === "owner" ? (
              <div className="flex justify-center px-2 py-4 sm:py-8">
                <div
                  data-testid="owner-view-frame"
                  className="w-full max-w-md overflow-hidden rounded-[28px] border border-zinc-300 bg-zinc-50 shadow-md sm:max-w-[420px]"
                  style={{ minHeight: "calc(100vh - 120px)" }}
                >
                  <OwnerPhone />
                </div>
              </div>
            ) : view === "settings" ? (
              <OrgSettingsModule
                storage={orgSettingsStorage}
                currentUser={{ role: user.role || "owner" }}
                onSaved={() => { onSessionRefresh(); setTimeout(() => setView("operator"), 1200); }}
                onCancel={() => setView("operator")}
              />
            ) : view === "accounts" ? (
              <LightAccountsModule
                storage={accountsStorage}
                currentUser={{ role: user.role || "owner" }}
              />
            ) : view === "walkins" ? (
              <IndividualsModule
                storage={individualsStorage}
                currentUser={{ role: user.role || "owner" }}
              />
            ) : view === "pulse" ? (
              <TeamPulse />
            ) : view === "import" ? (
              <ImportData
                currentUser={{ role: user.role }}
                onImported={onSessionRefresh}
                onNavigate={setView}
              />
            ) : (
              <TeamModule
                storage={teamStorage}
                currentUser={{
                  id: user.id,
                  name: user.name,
                  phone: user.phone,
                  role: user.role || "owner",
                }}
              />
            )}
          </div>
        </div>

        <EnquiryFlowSheet
          open={flowOpen}
          onClose={() => setFlowOpen(false)}
          onOpenStatuses={() => { setFlowOpen(false); setStatusesOpen(true); }}
          onOpenTypes={() => { setFlowOpen(false); setTypesOpen(true); }}
          onOpenSources={() => { setFlowOpen(false); setSourcesOpen(true); }}
          onOpenLost={() => { setFlowOpen(false); setLostReasonsOpen(true); }}
        />
        <ManageSourcesSheet open={sourcesOpen} onClose={() => setSourcesOpen(false)} canEdit={isAdmin} />
        <ManageTypesSheet open={typesOpen} onClose={() => setTypesOpen(false)} canEdit={isAdmin} />
        <ManageLostReasonsSheet open={lostReasonsOpen} onClose={() => setLostReasonsOpen(false)} canEdit={isAdmin} />
        <ManageStatusesSheet open={statusesOpen} onClose={() => setStatusesOpen(false)} canEdit={isAdmin} />
        <ManageLinesSheet open={linesOpen} onClose={() => setLinesOpen(false)} canEdit={isAdmin} />
        <UpgradeModal
          open={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          onSubscribed={onSessionRefresh}
        />
        <MyPlanSheet open={myPlanOpen} onClose={() => setMyPlanOpen(false)} billing={billing} />
      </div>
    </StateProvider>
  );

  // Ashraya help bot is on for everyone now. Solo users get a Solo-only intro
  // tour (passed via isSolo) so the spotlight never points at hidden menus.
  if (!ashrayaEnabled) return workspace;

  return (
    <AshraryaProvider config={ashrayaConfig}>
      {workspace}
      <AshrayaIntroLauncher user={user} config={ashrayaConfig} onSeenIntro={onSessionRefresh} isSolo={isSolo} />
      <AshrayaGuideLauncher config={ashrayaConfig} isSolo={isSolo} />
      <ResumeBanner theme={{ primary: "#e11d48" }} />
      <AshraryaWidget />
    </AshraryaProvider>
  );
};

const HeaderTab = ({ children, active, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
      active ? "bg-rose-600 text-white" : "text-zinc-600 hover:bg-zinc-200"
    }`}
  >
    {children}
  </button>
);
